import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Button,
  Table,
  Spin,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import apiService from '../services/apiService';

const { Title } = Typography;

const ValidationPage = () => {
  const { prNo } = useParams();
  const navigate = useNavigate();
  const [validationData, setValidationData] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);

  useEffect(() => {
    fetchValidationDetails();
  }, [prNo]);

  const fetchValidationDetails = async () => {
    try {
      setValidationLoading(true);
      const data = await apiService.getValidationDetails(prNo);
      const results = data.results || data;
      
      // Separate line items from other fields
      const lineItemFields = results.filter(item =>
        item.Field.includes('Line Item Description_line_item') ||
        item.Field.includes('Unit Price_line_item') ||
        item.Field.includes('Quantity_line_item') ||
        item.Field === 'Approval Doc Availability'
      );

      const otherFields = results.filter(item =>
        !item.Field.includes('Line Item Description_line_item') &&
        !item.Field.includes('Unit Price_line_item') &&
        !item.Field.includes('Quantity_line_item') &&
        item.Field !== 'Approval Doc Availability' &&
        !item.Field.toLowerCase().includes('commodity') &&
        !item.Field.toLowerCase().includes('approval authority') &&
        !item.Field.toLowerCase().includes('pr region check') &&
        item.Field !== 'Bill To Address' &&
        item.Field !== 'Ship To Address'&&
        item.Field !== 'Bill to Address' &&
        item.Field !== 'Ship to Address'&&
        item.Field !== 'PR Region Check' &&
        item.Field !== 'PR Attachment Count Check'
      );
      
      const formatValidationItem = (item, index) => {
        // Debug log to check Feedback field
        console.log('Field:', item.Field, 'Feedback:', item.Feedback, 'All item keys:', Object.keys(item));
        
        let matchStatus;
        
        // Special handling for HR PR with no attachments
        if (item.Feedback && 
            item.Feedback.includes('Not Applicable (HR PR with no attachments - Ariba data displayed for reference, no validation performed.)')) {
          matchStatus = "Match";
        }
        // Special handling for signature fields with "Not Applicable" (excluding "Unhandled")
        else if (item.Field && item.Field.toLowerCase().includes('signature') && 
            item.Feedback && 
            item.Feedback.toLowerCase().includes('not applicable') && 
            !item.Feedback.toLowerCase().includes('unhandled')) {
          matchStatus = "Match";
        } else {
          // Only use similarity score to determine match status
          // Special threshold for Service Period/Dates field
          const threshold = item.Field === 'Service Period/Dates' ? 0.6 : item.Field === 'CTS Entity Name' ? 0.35 : 0.5;

          if (typeof item.Similarity_Score === 'string' && item.Similarity_Score.includes(';')) {
            // Handle multiple scores, filter out N/A values
            const scores = item.Similarity_Score.split(';')
              .map(score => parseFloat(score.trim()))
              .filter(score => !isNaN(score));
            const hasMatch = scores.some(score => score >= threshold);
            matchStatus = hasMatch ? "Match" : "No Match";
          } else {
            // Handle single score
            const score = parseFloat(item.Similarity_Score);
            matchStatus = !isNaN(score) && score >= threshold ? "Match" : "No Match";
          }
        }
        
        // Format values for Payment Terms and Quantity fields to remove decimals
        // Add $ prefix for PR Amount Limit field
        let formattedAribaValue = item.Ariba_API_Value;
        let formattedAttachmentValue = item.Attachment_Value;
        
        if (item.Field === 'Payment Terms' || item.Field.startsWith('Quantity_line_item_') || item.Field === 'Quantity') {
          // Remove decimal places for these fields
          if (formattedAribaValue && typeof formattedAribaValue === 'string' && formattedAribaValue.includes(';')) {
            // Handle semicolon-separated values like "30.0; 30.0; 30.0; 30.0"
            formattedAribaValue = formattedAribaValue.split(';').map(val => {
              const num = parseFloat(val.trim());
              return !isNaN(num) ? Math.floor(num).toString() : val.trim();
            }).join('; ');
          } else if (formattedAribaValue && !isNaN(parseFloat(formattedAribaValue))) {
            formattedAribaValue = Math.floor(parseFloat(formattedAribaValue)).toString();
          }
          
          if (formattedAttachmentValue && typeof formattedAttachmentValue === 'string' && formattedAttachmentValue.includes(';')) {
            // Handle semicolon-separated values like "30.0; 30.0; 30.0; 30.0"
            formattedAttachmentValue = formattedAttachmentValue.split(';').map(val => {
              const num = parseFloat(val.trim());
              return !isNaN(num) ? Math.floor(num).toString() : val.trim();
            }).join('; ');
          } else if (formattedAttachmentValue && !isNaN(parseFloat(formattedAttachmentValue))) {
            formattedAttachmentValue = Math.floor(parseFloat(formattedAttachmentValue)).toString();
          }
        }
        
        // Add $ prefix for PR Amount Limit field
        if (item.Field === 'PR Amount Limit') {
          if (formattedAribaValue && formattedAribaValue !== '--' && formattedAribaValue !== 'Not Available') {
            formattedAribaValue = `$${formattedAribaValue}`;
          }
          if (formattedAttachmentValue && formattedAttachmentValue !== '--' && formattedAttachmentValue !== 'Not Available') {
            formattedAttachmentValue = `$${formattedAttachmentValue}`;
          }
        }
        
        return {
          key: index + 1,
          field: item.Field,
          fromAriba: formattedAribaValue,
          attachmentValue: formattedAttachmentValue,
          match: matchStatus,
          documentsMatching: item.Attachment_Source,
          remarks: item.Feedback || item.feedback || '--'
        };
      };
      
      const formattedOtherData = otherFields.map(formatValidationItem);
      const formattedLineItemData = lineItemFields.map(formatValidationItem);
      
      setValidationData({ otherFields: formattedOtherData, lineItems: formattedLineItemData });
    } catch (error) {
      message.error('Failed to load validation details');
    } finally {
      setValidationLoading(false);
    }
  };

  const checksColumns = [
    {
      title: "Field",
      dataIndex: "field",
      key: "field",
      width: 180,
      render: (text) => text === 'Approval Authority (Attachment)' ? 'Approval Authority' : text,
    },
    {
      title: "From Ariba",
      dataIndex: "fromAriba",
      key: "fromAriba",
      width: 130,
    },
    {
      title: "Attachment Value",
      dataIndex: "attachmentValue",
      key: "attachmentValue",
      width: 130,
    },
    {
      title: "Match Status",
      dataIndex: "match",
      key: "match",
      width: 100,
      render: (match) => {
        const color = match === "Match" ? "green" : "red";
        return <span style={{ color }}>{match}</span>;
      },
    },
    {
      title: "Documents Matching",
      dataIndex: "documentsMatching",
      key: "documentsMatching",
      width: 150,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 250,
      render: (text) => (
        <div style={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
          {text}
        </div>
      ),
    },
  ];

  const matchedFields = (validationData.otherFields || []).filter(
    (item) => item.match === "Match"
  ).length + (validationData.lineItems || []).filter(
    (item) => item.match === "Match"
  ).length;
  const totalFields = (validationData.otherFields || []).length + (validationData.lineItems || []).length;
  const matchPercentage = totalFields > 0 ? Math.round((matchedFields / totalFields) * 100) : 0;

  // Separate calculations for first table (other fields only)
  const otherFieldsMatchedFields = (validationData.otherFields || []).filter(
    (item) => item.match === "Match"
  ).length;
  const otherFieldsTotalFields = (validationData.otherFields || []).length;
  const otherFieldsMatchPercentage = otherFieldsTotalFields > 0 ? Math.round((otherFieldsMatchedFields / otherFieldsTotalFields) * 100) : 0;

  const lineItemMatchedFields = (validationData.lineItems || []).filter(
    (item) => item.match === "Match"
  ).length;
  const lineItemTotalFields = (validationData.lineItems || []).length;
  const lineItemMatchPercentage = lineItemTotalFields > 0 ? Math.round((lineItemMatchedFields / lineItemTotalFields) * 100) : 0;

  // Get score color based on percentage
  const getScoreColor = (percentage) => {
    if (percentage >= 80) return "#52c41a"; // green
    if (percentage >= 50) return "#faad14"; // orange
    return "#ff4d4f"; // red
  };

  return (
    <div className="pr-details-container">
      <div className="pr-details-header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="back-button"
        >
          Back to PR Details
        </Button>
        <div className="flex-between" style={{ width: "100%" }}>
          <Title level={3} className="pr-details-title">
            <FileTextOutlined /> Field Validation Checks - PR {prNo}
          </Title>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text strong>
            Validation Summary: 
            <span style={{ color: getScoreColor(matchPercentage), fontWeight: '600' }}>
              {matchedFields}/{totalFields} Fields Availability ({matchPercentage}%)
            </span>
          </Typography.Text>
        </div>
      </div>

      <Card className="pr-info-card">
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text strong>
            Field Validation Summary:  
            <span style={{ color: getScoreColor(otherFieldsMatchPercentage), fontWeight: '600' }}>
               {otherFieldsMatchedFields}/{otherFieldsTotalFields} Fields Availability ({otherFieldsMatchPercentage}%)
            </span>
          </Typography.Text>
        </div>
        <Spin spinning={validationLoading} tip="Loading validation data...">
          <Table
            columns={checksColumns}
            dataSource={validationData.otherFields || []}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
            size="small"
            style={{ fontSize: "12px" }}
            scroll={{ x: 1200 }}
          />
        </Spin>
      </Card>

      {(validationData.lineItems || []).length > 0 && (
        <Card className="pr-info-card" style={{ marginTop: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Title level={4}>
              <FileTextOutlined /> Line Items
            </Title>
            <Typography.Text strong>
              Line Items Summary:  
              <span style={{ color: getScoreColor(lineItemMatchPercentage), fontWeight: '600' }}>
                {lineItemMatchedFields}/{lineItemTotalFields} Fields Availability ({lineItemMatchPercentage}%)
              </span>
            </Typography.Text>
          </div>
          <Spin spinning={validationLoading} tip="Loading line item data...">
            <Table
              columns={checksColumns}
              dataSource={validationData.lineItems || []}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              }}
              size="small"
              style={{ fontSize: "12px" }}
              scroll={{ x: 1200 }}
            />
          </Spin>
        </Card>
      )}
    </div>
  );
};

export default ValidationPage;